import { type ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { AgentDesktopAgentView, CentralTeamDetailView, CentralTeamListingView, CentralTeamMemberView, CentralTeamView } from '@vello/api-client';
import {
  Badge, Button, Checkbox, Drawer, DrawerBody, DrawerFooter, DrawerHeader, EmptyState, Field, FilterBar, ListTable, Notice, PageHeader, Row, SectionTitle, Select, Stack, Switch, Text, TextInput, type Column, type TimelineEntry,
} from '@vello/web-theme';
import {
  TEAM_CHANNEL_CHOICES, TEAM_FUNCTION_CHOICES, TEAM_WEEKDAYS, addWindow, centralTeamAuthority, distinctTimeZones, draftFromTeam, draftIssue, draftPatch, formatMarketInstant, functionHasNoChannels, functionIsAlwaysOpen,
  emptyTeamsSummary, groupHours, hasChanges, staffingTone, teamStaffing, teamMarketsOfCountry, newTeamDraft, openNowByMarket, openNowSummary, parseLanguages, removeWindow, replaceWindow, servedMarkets, teamCountries, teamDisplayName, teamStatusTone, windowsText,
  type MarketOpenNow, type TeamDraft,
} from '../../src/index';
import { useAdmin } from '../session';
import { useConsole } from '../console';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { useCountryName } from '../i18n/country-name';
import { DrawerStack } from '../components/drawer-stack';
import { HistoryTimeline } from '../components/history-timeline';

/**
 * Operações › Equipes da Central (D632, doc 25 section 7, owner Q-367): the teams of the Digital Central per country - a function (service,
 * finance, commercial, provider registration, safety, supervision), the languages and channels it answers, its weekly hours and who belongs to it.
 * The list shows a team per row; a row opens the team in a drawer on the right (D619) where an administrator edits the hours, languages,
 * channels and name under a compare-and-set version and manages the members (a person can be in several teams; the server admits only a person
 * whose desk grant already reaches the team). The hours are LOCAL wall-clock windows: the screen shows each served city's own timezone and
 * whether the team is open there now. Every code is a word through the vocabulary catalog; layout and type come from web-theme (no inline style).
 */

type Msg = { readonly tone: 'success' | 'danger'; readonly text: string };
type StatusFilter = '' | 'ACTIVE' | 'ARCHIVED';

interface CreateState {
  readonly country: string;
  readonly fn: string;
  readonly draft: TeamDraft;
}

/** The name of a timezone in the console's language ("Japan Standard Time"); the zone id only when the platform cannot name it. */
function zoneName(zone: string, lang: string, atMs: number): string {
  try {
    const part = new Intl.DateTimeFormat(lang, { timeZone: zone, timeZoneName: 'long' }).formatToParts(new Date(atMs)).find((p) => p.type === 'timeZoneName')?.value;
    return part ?? zone;
  } catch {
    return zone;
  }
}

export function CentralTeamsScreen(): ReactElement {
  const { runtime, markets, grants } = useAdmin();
  const { t, lang } = useT();
  const { v, explain, fail, language } = useVocab();
  const { confirm } = useConsole();
  const countryName = useCountryName();
  const authority = centralTeamAuthority(grants);

  const [teams, setTeams] = useState<readonly CentralTeamListingView[] | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [country, setCountry] = useState('');
  const [fn, setFn] = useState('');
  const [status, setStatus] = useState<StatusFilter>('ACTIVE');
  const [notice, setNotice] = useState<Msg | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState<CentralTeamDetailView | undefined>(undefined);
  const [draft, setDraft] = useState<TeamDraft | undefined>(undefined);
  const [creating, setCreating] = useState<CreateState | undefined>(undefined);
  const [agents, setAgents] = useState<readonly AgentDesktopAgentView[] | 'unavailable' | undefined>(undefined);
  const [memberId, setMemberId] = useState('');
  const [memberRole, setMemberRole] = useState('member');
  const readSeq = useRef(0);

  const refuse = useCallback(
    (e: { readonly code: string; readonly reasonCode?: string | undefined; readonly params?: Readonly<Record<string, string | number>> | undefined }): string => explain(e) ?? t('lmap.err.generic', { code: fail(e) }),
    [explain, fail, t],
  );

  const load = useCallback(async (): Promise<void> => {
    const seq = ++readSeq.current;
    const res = await runtime.centralTeams.list({ ...(country !== '' ? { country } : {}), ...(fn !== '' ? { function: fn } : {}), ...(status !== '' ? { status } : {}) });
    if (seq !== readSeq.current) return;
    if (res.ok) {
      setTeams(res.value.teams);
      setError(undefined);
    } else {
      // A refused read keeps the list unknown: the screen says it could not load, never "no teams".
      setError(refuse(res.error));
    }
  }, [runtime, country, fn, status, refuse]);

  useEffect(() => {
    void load();
  }, [load]);

  const openTeam = useCallback(
    async (teamId: string): Promise<void> => {
      setCreating(undefined);
      const res = await runtime.centralTeams.get(teamId);
      if (res.ok) {
        setDetail(res.value);
        setDraft(draftFromTeam(res.value.team));
        setMemberId('');
        setMemberRole('member');
      } else {
        setNotice({ tone: 'danger', text: refuse(res.error) });
      }
    },
    [runtime, refuse],
  );

  /** Re-read the open team and the list after a change (the version moved). */
  const refreshOpen = useCallback(
    async (teamId: string): Promise<void> => {
      const res = await runtime.centralTeams.get(teamId);
      if (res.ok) {
        setDetail(res.value);
        setDraft(draftFromTeam(res.value.team));
      }
      await load();
    },
    [runtime, load],
  );

  // The people a team can be given: the desk directory the supervisor may name. Read once when a team the operator manages opens.
  const openId = detail?.team.teamId;
  useEffect(() => {
    if (!authority.mayManage || openId === undefined || agents !== undefined) return;
    void runtime.agentDesktop.listAgents().then((res) => setAgents(res.ok ? res.value.agents : 'unavailable'));
  }, [runtime, authority.mayManage, openId, agents]);

  const closeDrawer = (): void => {
    setDetail(undefined);
    setDraft(undefined);
    setCreating(undefined);
  };

  const nowIso = new Date().toISOString();
  const nowMs = Date.parse(nowIso);
  const functionLabel = (code: string): string => v('central_teams.function', code);
  const nameOf = (team: CentralTeamView): string => teamDisplayName(team, functionLabel(team.function));
  const dayLabel = (day: string): string => v('central_teams.weekday_short', day);
  const dayRange = (days: readonly string[]): string => (days.length === 1 ? dayLabel(days[0] as string) : `${dayLabel(days[0] as string)}–${dayLabel(days[days.length - 1] as string)}`);
  const languageNames = (tags: readonly string[]): string => tags.map((tag) => language(tag)).join(', ');
  const marketNames = (team: CentralTeamView): string => servedMarkets(team, markets).map((m) => m.name).join(', ');

  const hoursLines = (team: CentralTeamView): readonly string[] =>
    team.alwaysOpen ? [t('cteams.hours.always')] : groupHours(team.weeklyHours).map((g) => t('cteams.hours.line', { days: dayRange(g.days), windows: windowsText(g.windows) }));

  const nowBadge = (team: CentralTeamView): ReactElement => {
    const summary = openNowSummary(openNowByMarket(team, markets, nowIso));
    if (summary.known === 0) return <Badge tone="neutral">{t('cteams.now.unknown')}</Badge>;
    if (summary.open === summary.known) return <Badge tone="success">{t('cteams.now.open')}</Badge>;
    if (summary.open === 0) return <Badge tone="neutral">{t('cteams.now.closed')}</Badge>;
    return <Badge tone="warning">{t('cteams.now.partial', { open: summary.open, total: summary.known })}</Badge>;
  };

  const countries = useMemo(() => teamCountries(markets, teams ?? []), [markets, teams]);
  const countrySummary = useMemo(() => new Set((teams ?? []).map((x) => x.countryCode)).size, [teams]);

  const columns: readonly Column<CentralTeamListingView>[] = [
    {
      key: 'team',
      header: t('cteams.col.team'),
      width: 'minmax(220px, 1.4fr)',
      render: (team) => (
        <Stack gap={1}>
          <Text as="strong">{nameOf(team)}</Text>
          <Text role="caption" tone="secondary">
            {countryName(team.countryCode)} · {team.marketIds.length === 0 ? t('cteams.scope.country') : t('cteams.scope.markets', { markets: marketNames(team) })}
          </Text>
          {team.isDefault && <Text role="caption" tone="secondary">{t('cteams.default')}</Text>}
        </Stack>
      ),
    },
    {
      key: 'hours',
      header: t('cteams.col.hours'),
      width: 'minmax(240px, 1.6fr)',
      render: (team) => (
        <Stack gap={1}>
          {hoursLines(team).map((line) => (
            <Text key={line} className="tnum">{line}</Text>
          ))}
          {nowBadge(team)}
        </Stack>
      ),
    },
    {
      key: 'languages',
      header: t('cteams.col.languages'),
      width: 'minmax(140px, 1fr)',
      render: (team) => <Text>{languageNames(team.languages)}</Text>,
    },
    {
      key: 'channels',
      header: t('cteams.col.channels'),
      width: 'minmax(180px, 1.2fr)',
      render: (team) =>
        team.channels.length === 0 ? (
          <Text role="caption" tone="secondary">—</Text>
        ) : (
          <Row gap={1} wrap>
            {team.channels.map((c) => (
              <Badge key={c}>{v('central_teams.channel', c)}</Badge>
            ))}
          </Row>
        ),
    },
    {
      key: 'members',
      header: t('cteams.col.members'),
      width: 'minmax(130px, 0.8fr)',
      render: (team) => (
        <Stack gap={1}>
          <Text className="tnum">{team.memberCount === 0 ? t('cteams.members.none') : t('cteams.members.count', { n: team.memberCount })}</Text>
          {teamStaffing(team) !== 'staffed' && <Badge tone={staffingTone(teamStaffing(team))}>{t(teamStaffing(team) === 'empty_24h' ? 'cteams.staffing.empty24h' : 'cteams.staffing.empty')}</Badge>}
          {team.coordinatorCount > 0 && <Text role="caption" tone="secondary" className="tnum">{t('cteams.members.coordinators', { n: team.coordinatorCount })}</Text>}
        </Stack>
      ),
    },
    {
      key: 'state',
      header: t('cteams.col.state'),
      width: 'minmax(110px, 0.7fr)',
      render: (team) => <Badge tone={teamStatusTone(team.status)}>{v('central_teams.status', team.status)}</Badge>,
    },
  ];

  // ---- writes -----------------------------------------------------------------------------------------------------

  const run = async (action: () => Promise<{ readonly ok: true; readonly done: string } | { readonly ok: false; readonly text: string }>): Promise<boolean> => {
    setBusy(true);
    setNotice(undefined);
    const result = await action();
    setBusy(false);
    setNotice(result.ok ? { tone: 'success', text: result.done } : { tone: 'danger', text: result.text });
    return result.ok;
  };

  const save = async (): Promise<void> => {
    if (detail === undefined || draft === undefined) return;
    const patch = draftPatch(detail.team, draft);
    if (!hasChanges(patch)) return;
    await run(async () => {
      const res = await runtime.centralTeams.update(detail.team.teamId, detail.team.version, patch);
      return res.ok ? { ok: true as const, done: t('cteams.done.saved') } : { ok: false as const, text: refuse(res.error) };
    });
    await refreshOpen(detail.team.teamId);
  };

  const archiveOrRestore = async (): Promise<void> => {
    if (detail === undefined) return;
    const team = detail.team;
    if (team.status === 'ACTIVE') {
      const answer = await confirm({ title: t('cteams.archive.title', { name: nameOf(team) }), body: t('cteams.archive.body'), confirmLabel: t('cteams.archive.confirm'), cancelLabel: t('ui.cancel'), tone: 'danger' });
      if (!answer.confirmed) return;
    }
    await run(async () => {
      const res = team.status === 'ACTIVE' ? await runtime.centralTeams.archive(team.teamId, team.version) : await runtime.centralTeams.restore(team.teamId, team.version);
      return res.ok ? { ok: true as const, done: t(team.status === 'ACTIVE' ? 'cteams.done.archived' : 'cteams.done.restored') } : { ok: false as const, text: refuse(res.error) };
    });
    await refreshOpen(team.teamId);
  };

  const addPerson = async (): Promise<void> => {
    if (detail === undefined || memberId === '') return;
    const teamId = detail.team.teamId;
    await run(async () => {
      const res = await runtime.centralTeams.addMember(teamId, memberId, memberRole);
      return res.ok ? { ok: true as const, done: t('cteams.done.memberAdded') } : { ok: false as const, text: refuse(res.error) };
    });
    setMemberId('');
    await refreshOpen(teamId);
  };

  const changeRole = async (member: CentralTeamMemberView, role: string): Promise<void> => {
    if (detail === undefined || role === member.role) return;
    const teamId = detail.team.teamId;
    await run(async () => {
      const res = await runtime.centralTeams.addMember(teamId, member.operatorId, role);
      return res.ok ? { ok: true as const, done: t('cteams.done.roleChanged') } : { ok: false as const, text: refuse(res.error) };
    });
    await refreshOpen(teamId);
  };

  const removePerson = async (member: CentralTeamMemberView): Promise<void> => {
    if (detail === undefined) return;
    const teamId = detail.team.teamId;
    await run(async () => {
      const res = await runtime.centralTeams.removeMember(teamId, member.operatorId);
      return res.ok ? { ok: true as const, done: t('cteams.done.memberRemoved') } : { ok: false as const, text: refuse(res.error) };
    });
    await refreshOpen(teamId);
  };

  const startCreate = (): void => {
    const first = countries[0] ?? '';
    setDetail(undefined);
    setDraft(undefined);
    setNotice(undefined);
    setCreating({ country: country !== '' ? country : first, fn: '', draft: newTeamDraft('atendimento', []) });
  };

  const languagesOfCountry = (cc: string): readonly string[] => (teams ?? []).find((x) => x.countryCode === cc)?.languages ?? [];

  const create = async (): Promise<void> => {
    if (creating === undefined || creating.fn === '' || creating.country === '') return;
    const d = creating.draft;
    const made = await run(async () => {
      const res = await runtime.centralTeams.create({
        function: creating.fn,
        countryCode: creating.country,
        marketIds: d.marketIds,
        languages: parseLanguages(d.languagesText),
        channels: TEAM_CHANNEL_CHOICES.filter((c) => d.channels.includes(c)),
        alwaysOpen: d.alwaysOpen,
        weeklyHours: d.alwaysOpen ? [] : d.windows,
        ...(d.nameOverride.trim() !== '' ? { nameOverride: d.nameOverride.trim() } : {}),
      });
      return res.ok ? { ok: true as const, done: t('cteams.done.created') } : { ok: false as const, text: refuse(res.error) };
    });
    await load();
    if (made) setCreating(undefined);
  };

  // ---- the editor (hours, languages, channels, name), shared by the team drawer and the create drawer -----------------

  const issueText = (fnCode: string, d: TeamDraft): string | undefined => {
    const issue = draftIssue(d, fnCode);
    return issue === undefined ? undefined : refuse({ code: 'BAD_REQUEST', reasonCode: issue });
  };

  const renderEditor = (opts: { readonly idPrefix: string; readonly fnCode: string; readonly d: TeamDraft; readonly onChange: (next: TeamDraft) => void; readonly canEdit: boolean; readonly now: readonly MarketOpenNow[] }): ReactElement => {
    const { idPrefix, fnCode, d, onChange, canEdit, now } = opts;
    const locked = functionIsAlwaysOpen(fnCode);
    const noChannels = functionHasNoChannels(fnCode);
    const zones = distinctTimeZones(now.map((n) => n.market));
    const names = languageNames(parseLanguages(d.languagesText));
    return (
      <>
        <Field label={t('cteams.name.label')} htmlFor={`${idPrefix}-name`} hint={t('cteams.name.hint')}>
          <TextInput id={`${idPrefix}-name`} value={d.nameOverride} maxLength={80} disabled={!canEdit} placeholder={fnCode !== '' ? functionLabel(fnCode) : ''} onChange={(e) => onChange({ ...d, nameOverride: e.target.value })} />
        </Field>

        <Stack gap={3}>
          <SectionTitle>{t('cteams.hours.title')}</SectionTitle>
          <Text role="caption" tone="secondary" as="p">{t('cteams.hours.hint')}</Text>
          <Switch checked={d.alwaysOpen} disabled={!canEdit || locked} label={t('cteams.hours.alwaysLabel')} onChange={(next) => onChange({ ...d, alwaysOpen: next, windows: next ? [] : d.windows.length > 0 ? d.windows : newTeamDraft('atendimento', []).windows })} />
          {locked && <Text role="caption" tone="secondary" as="p">{t('cteams.hours.alwaysLocked')}</Text>}
          {!d.alwaysOpen &&
            TEAM_WEEKDAYS.map((day) => {
              const mine = d.windows.map((w, index) => ({ w, index })).filter((x) => x.w.day === day);
              return (
                <Stack key={day} gap={2}>
                  <Text as="strong">{v('central_teams.weekday', day)}</Text>
                  {mine.length === 0 && <Text role="caption" tone="secondary">{t('cteams.hours.dayOff')}</Text>}
                  {mine.map(({ w, index }) => (
                    <Row key={index} gap={2} align="end" wrap>
                      <Field label={t('cteams.hours.start')} htmlFor={`${idPrefix}-${day}-${index}-start`}>
                        <TextInput id={`${idPrefix}-${day}-${index}-start`} type="time" className="tnum" value={w.start} disabled={!canEdit} onChange={(e) => onChange({ ...d, windows: replaceWindow(d.windows, index, { ...w, start: e.target.value }) })} />
                      </Field>
                      <Field label={t('cteams.hours.end')} htmlFor={`${idPrefix}-${day}-${index}-end`}>
                        <TextInput id={`${idPrefix}-${day}-${index}-end`} type="time" className="tnum" value={w.end === '24:00' ? '23:59' : w.end} disabled={!canEdit} onChange={(e) => onChange({ ...d, windows: replaceWindow(d.windows, index, { ...w, end: e.target.value }) })} />
                      </Field>
                      {canEdit && <Button variant="ghost" aria-label={`${t('cteams.hours.remove')} - ${v('central_teams.weekday', day)}`} onClick={() => onChange({ ...d, windows: removeWindow(d.windows, index) })}>{t('cteams.hours.remove')}</Button>}
                    </Row>
                  ))}
                  {canEdit && <Row gap={2}><Button variant="secondary" aria-label={`${t('cteams.hours.add')} - ${v('central_teams.weekday', day)}`} onClick={() => onChange({ ...d, windows: addWindow(d.windows, day) })}>{t('cteams.hours.add')}</Button></Row>}
                </Stack>
              );
            })}
        </Stack>

        <Stack gap={2}>
          <SectionTitle>{t('cteams.zones.title')}</SectionTitle>
          {now.length === 0 && <Text role="caption" tone="secondary">{t('cteams.zones.none')}</Text>}
          {now.map((n) => (
            <Row key={n.market.id} gap={2} wrap align="center">
              <Text className="tnum">
                {n.market.timeZone !== undefined && n.localTime !== undefined ? t('cteams.zones.row', { name: n.market.name, zone: zoneName(n.market.timeZone, lang, nowMs), time: n.localTime }) : t('cteams.zones.rowUnknown', { name: n.market.name })}
              </Text>
              {n.open !== undefined && <Badge tone={n.open ? 'success' : 'neutral'}>{n.open ? t('cteams.now.open') : t('cteams.now.closed')}</Badge>}
            </Row>
          ))}
          {zones.length > 1 && <Text role="caption" tone="secondary">{t('cteams.hours.zones', { zones: zones.map((z) => zoneName(z, lang, nowMs)).join(', ') })}</Text>}
        </Stack>

        <Field label={t('cteams.languages.label')} htmlFor={`${idPrefix}-languages`} hint={t('cteams.languages.hint')} {...(names !== '' ? { footer: <Text role="caption" tone="secondary">{t('cteams.languages.preview', { names })}</Text> } : {})}>
          <TextInput id={`${idPrefix}-languages`} value={d.languagesText} disabled={!canEdit} autoComplete="off" onChange={(e) => onChange({ ...d, languagesText: e.target.value })} />
        </Field>

        <Stack gap={2}>
          <SectionTitle>{t('cteams.channels.label')}</SectionTitle>
          {noChannels ? (
            <Text role="caption" tone="secondary">{t('cteams.channels.none')}</Text>
          ) : (
            <Row gap={4} wrap>
              {TEAM_CHANNEL_CHOICES.map((c) => (
                <Checkbox key={c} checked={d.channels.includes(c)} disabled={!canEdit} label={v('central_teams.channel', c)} onChange={(on) => onChange({ ...d, channels: on ? [...d.channels, c] : d.channels.filter((x) => x !== c) })} />
              ))}
            </Row>
          )}
        </Stack>
      </>
    );
  };

  // ---- the team drawer ----------------------------------------------------------------------------------------------

  const memberColumns = (d: CentralTeamDetailView, zone: string | undefined): readonly Column<CentralTeamMemberView>[] => [
    {
      key: 'person',
      header: t('cteams.members.col.person'),
      width: 'minmax(160px, 1.4fr)',
      render: (m) => <Text as="strong">{d.operators[m.operatorId] ?? t('cteams.members.unnamed')}</Text>,
    },
    {
      key: 'role',
      header: t('cteams.members.col.role'),
      width: 'minmax(150px, 1fr)',
      render: (m) =>
        authority.mayManage && d.team.status === 'ACTIVE' ? (
          <Select aria-label={t('cteams.members.roleOf', { name: d.operators[m.operatorId] ?? t('cteams.members.unnamed') })} value={m.role} disabled={busy} onChange={(e) => void changeRole(m, e.target.value)}>
            {['member', 'coordinator'].map((r) => (
              <option key={r} value={r}>{v('central_teams.member_role', r)}</option>
            ))}
          </Select>
        ) : (
          <Badge>{v('central_teams.member_role', m.role)}</Badge>
        ),
    },
    {
      key: 'since',
      header: t('cteams.members.col.since'),
      width: 'minmax(130px, 1fr)',
      render: (m) => <Text role="caption" tone="secondary" className="tnum">{formatMarketInstant(m.addedAt, zone, lang, 'date')}</Text>,
    },
    {
      key: 'why',
      header: t('cteams.members.col.why'),
      width: 'minmax(200px, 1.6fr)',
      render: (m) => <Text role="caption" tone="secondary">{v('central_teams.admission_reason', m.reasonCode)}</Text>,
    },
  ];

  /** The team's history as a timeline (newest first): archiving and removing a person are amber. */
  const historyEntries = (d: CentralTeamDetailView, zone: string | undefined): readonly TimelineEntry[] =>
    d.events.map((ev) => {
      const changed = ev.detail['changed'];
      const detail = ev.reasonCode !== undefined
        ? v('central_teams.admission_reason', ev.reasonCode)
        : Array.isArray(changed) && changed.length > 0 ? t('cteams.history.fields', { fields: (changed as readonly string[]).map((f) => t(`cteams.field.${f}`)).join(', ') }) : undefined;
      return {
        id: ev.eventId,
        tone: ev.kind === 'TEAM_ARCHIVED' || ev.kind === 'MEMBER_REMOVED' ? 'warning' : 'neutral',
        title: v('central_teams.event_kind', ev.kind),
        ...(detail === undefined ? {} : { detail }),
        meta: `${formatMarketInstant(ev.at, zone, lang)} · ${ev.actor === 'system' ? t('cteams.history.system') : d.operators[ev.actor] ?? t('cteams.members.unnamed')}`,
      };
    });

  const teamDrawer = (d: CentralTeamDetailView, current: TeamDraft): ReactElement => {
    const team = d.team;
    const archived = team.status === 'ARCHIVED';
    const canEdit = authority.mayManage && !archived;
    const now = openNowByMarket(team, markets, nowIso);
    const issue = issueText(team.function, current);
    const patch = draftPatch(team, current);
    const memberIds = new Set(d.members.map((m) => m.operatorId));
    const pickable = Array.isArray(agents) ? (agents as readonly AgentDesktopAgentView[]).filter((a) => !memberIds.has(a.userId)) : [];
    const zone = distinctTimeZones(now.map((n) => n.market))[0];
    return (
      <>
        <DrawerHeader
          titleId="cteams-title"
          title={t('cteams.drawer.title', { name: nameOf(team) })}
          subtitle={
            <Row gap={2} wrap align="center">
              <Badge tone={teamStatusTone(team.status)}>{v('central_teams.status', team.status)}</Badge>
              <Text role="caption" tone="secondary">{t('cteams.drawer.country', { country: countryName(team.countryCode) })}</Text>
            </Row>
          }
          onClose={closeDrawer}
          closeLabel={t('ui.close')}
        />
        <DrawerBody>
          <DrawerStack>
            {notice !== undefined && <Notice tone={notice.tone}>{notice.text}</Notice>}
            {archived && <Notice tone="neutral">{t('cteams.archivedNote')}</Notice>}
            {!archived && d.members.length === 0 && <Notice tone={team.alwaysOpen ? 'danger' : 'warning'}>{t(team.alwaysOpen ? 'cteams.staffing.drawer24h' : 'cteams.staffing.drawer')}</Notice>}
            {renderEditor({ idPrefix: 'cteams', fnCode: team.function, d: current, onChange: setDraft, canEdit, now })}
            {canEdit && issue !== undefined && <Notice tone="danger">{issue}</Notice>}

            <Stack gap={3}>
              <SectionTitle>{t('cteams.members.title')}</SectionTitle>
              <Text role="caption" tone="secondary" as="p">{t('cteams.members.hint')}</Text>
              <ListTable
                columns={memberColumns(d, zone)}
                rows={d.members}
                rowKey={(m) => m.operatorId}
                actions={
                  authority.mayManage
                    ? (m) => <Button variant="ghost" disabled={busy} aria-label={t('cteams.members.removeNamed', { name: d.operators[m.operatorId] ?? t('cteams.members.unnamed') })} onClick={() => void removePerson(m)}>{t('cteams.members.remove')}</Button>
                    : undefined
                }
                actionsWidth="max-content"
                loading={false}
                loadingLabel={t('ui.loading')}
                empty={<EmptyState title={t('cteams.members.empty')} />}
                minWidth={620}
              />
              {canEdit && (
                <Stack gap={3}>
                  <SectionTitle>{t('cteams.members.add.title')}</SectionTitle>
                  {agents === 'unavailable' ? (
                    <Notice tone="neutral">{t('cteams.members.add.unavailable')}</Notice>
                  ) : agents !== undefined && pickable.length === 0 ? (
                    <Text role="caption" tone="secondary">{t('cteams.members.add.noAgents')}</Text>
                  ) : (
                    <FilterBar gap={3}>
                      <Field label={t('cteams.members.add.person')} htmlFor="cteams-add-person">
                        <Select id="cteams-add-person" value={memberId} disabled={busy || agents === undefined} onChange={(e) => setMemberId(e.target.value)}>
                          <option value="">{t('cteams.members.add.choose')}</option>
                          {pickable.map((a) => (
                            <option key={a.userId} value={a.userId}>{a.name}</option>
                          ))}
                        </Select>
                      </Field>
                      <Field label={t('cteams.members.add.role')} htmlFor="cteams-add-role">
                        <Select id="cteams-add-role" value={memberRole} onChange={(e) => setMemberRole(e.target.value)}>
                          {['member', 'coordinator'].map((r) => (
                            <option key={r} value={r}>{v('central_teams.member_role', r)}</option>
                          ))}
                        </Select>
                      </Field>
                      <Button variant="secondary" disabled={busy || memberId === ''} onClick={() => void addPerson()}>{t('cteams.members.add.submit')}</Button>
                    </FilterBar>
                  )}
                </Stack>
              )}
            </Stack>

            <Stack gap={3}>
              <SectionTitle>{t('cteams.history.title')}</SectionTitle>
              <HistoryTimeline entries={historyEntries(d, zone)} emptyTitle={t('cteams.history.empty')} />
            </Stack>
          </DrawerStack>
        </DrawerBody>
        <DrawerFooter>
          <Button variant="ghost" onClick={closeDrawer}>{t('cteams.close')}</Button>
          {authority.mayManage && (
            <Button variant="secondary" disabled={busy} onClick={() => void archiveOrRestore()}>{archived ? t('cteams.restore') : t('cteams.archive')}</Button>
          )}
          {canEdit && (
            <Button variant="primary" disabled={busy || issue !== undefined || !hasChanges(patch)} onClick={() => void save()}>{t('cteams.save')}</Button>
          )}
        </DrawerFooter>
      </>
    );
  };

  // ---- the create drawer ---------------------------------------------------------------------------------------------

  const createDrawer = (c: CreateState): ReactElement => {
    const cityChoices = teamMarketsOfCountry(markets, c.country);
    const now: readonly MarketOpenNow[] = openNowByMarket({ alwaysOpen: c.draft.alwaysOpen, weeklyHours: c.draft.windows, countryCode: c.country, marketIds: c.draft.marketIds }, markets, nowIso);
    const issue = c.fn === '' ? undefined : issueText(c.fn, c.draft);
    return (
      <>
        <DrawerHeader titleId="cteams-title" title={t('cteams.create.title')} onClose={closeDrawer} closeLabel={t('ui.close')} />
        <DrawerBody>
          <DrawerStack>
            {notice !== undefined && <Notice tone={notice.tone}>{notice.text}</Notice>}
            <FilterBar gap={3}>
              <Field label={t('cteams.create.country')} htmlFor="cteams-new-country">
                <Select id="cteams-new-country" value={c.country} onChange={(e) => setCreating({ ...c, country: e.target.value, draft: { ...c.draft, marketIds: [], languagesText: languagesOfCountry(e.target.value).join(', ') } })}>
                  <option value="">{t('cteams.create.country.choose')}</option>
                  {countries.map((cc) => (
                    <option key={cc} value={cc}>{countryName(cc)}</option>
                  ))}
                </Select>
              </Field>
              <Field label={t('cteams.create.function')} htmlFor="cteams-new-function">
                <Select id="cteams-new-function" value={c.fn} onChange={(e) => setCreating({ ...c, fn: e.target.value, draft: { ...newTeamDraft(e.target.value === '' ? 'atendimento' : e.target.value, languagesOfCountry(c.country)), marketIds: c.draft.marketIds, nameOverride: c.draft.nameOverride } })}>
                  <option value="">{t('cteams.create.function.choose')}</option>
                  {TEAM_FUNCTION_CHOICES.map((f) => (
                    <option key={f} value={f}>{functionLabel(f)}</option>
                  ))}
                </Select>
              </Field>
            </FilterBar>
            {cityChoices.length > 0 && (
              <Stack gap={2}>
                <SectionTitle>{t('cteams.create.markets')}</SectionTitle>
                <Text role="caption" tone="secondary" as="p">{t('cteams.create.markets.hint')}</Text>
                <Row gap={4} wrap>
                  {cityChoices.map((m) => (
                    <Checkbox key={m.id} checked={c.draft.marketIds.includes(m.id)} label={m.name} onChange={(on) => setCreating({ ...c, draft: { ...c.draft, marketIds: on ? [...c.draft.marketIds, m.id] : c.draft.marketIds.filter((x) => x !== m.id) } })} />
                  ))}
                </Row>
              </Stack>
            )}
            {c.fn !== '' && renderEditor({ idPrefix: 'cteams-new', fnCode: c.fn, d: c.draft, onChange: (next) => setCreating({ ...c, draft: next }), canEdit: true, now })}
            {issue !== undefined && <Notice tone="danger">{issue}</Notice>}
          </DrawerStack>
        </DrawerBody>
        <DrawerFooter>
          <Button variant="ghost" onClick={closeDrawer}>{t('ui.cancel')}</Button>
          <Button variant="primary" disabled={busy || c.fn === '' || c.country === '' || issue !== undefined} onClick={() => void create()}>{t('cteams.create.submit')}</Button>
        </DrawerFooter>
      </>
    );
  };

  const drawerOpen = detail !== undefined || creating !== undefined;

  return (
    <Stack gap={5}>
      <PageHeader title={t('cteams.title')} subtitle={t('cteams.subtitle')} actions={authority.mayManage ? <Button variant="primary" onClick={startCreate}>{t('cteams.create.open')}</Button> : undefined} />

      {!authority.mayManage && <Notice tone="neutral">{t('cteams.readOnly')}</Notice>}
      {error !== undefined && <Notice tone="danger">{error}</Notice>}
      {notice !== undefined && !drawerOpen && <Notice tone={notice.tone}>{notice.text}</Notice>}
      {teams !== undefined && emptyTeamsSummary(teams).empty24h > 0 && <Notice tone="danger">{t('cteams.staffing.banner24h', { n: emptyTeamsSummary(teams).empty24h })}</Notice>}
      {teams !== undefined && emptyTeamsSummary(teams).empty > emptyTeamsSummary(teams).empty24h && <Notice tone="warning">{t('cteams.staffing.banner', { n: emptyTeamsSummary(teams).empty - emptyTeamsSummary(teams).empty24h })}</Notice>}

      <FilterBar gap={4} label={t('cteams.filters')}>
        <Field label={t('cteams.filter.country')} htmlFor="cteams-country">
          <Select id="cteams-country" value={country} onChange={(e) => setCountry(e.target.value)}>
            <option value="">{t('cteams.filter.country.all')}</option>
            {countries.map((cc) => (
              <option key={cc} value={cc}>{countryName(cc)}</option>
            ))}
          </Select>
        </Field>
        <Field label={t('cteams.filter.function')} htmlFor="cteams-function">
          <Select id="cteams-function" value={fn} onChange={(e) => setFn(e.target.value)}>
            <option value="">{t('cteams.filter.function.all')}</option>
            {TEAM_FUNCTION_CHOICES.map((f) => (
              <option key={f} value={f}>{functionLabel(f)}</option>
            ))}
          </Select>
        </Field>
        <Field label={t('cteams.filter.status')} htmlFor="cteams-status">
          <Select id="cteams-status" value={status} onChange={(e) => setStatus(e.target.value as StatusFilter)}>
            <option value="ACTIVE">{t('cteams.filter.status.active')}</option>
            <option value="ARCHIVED">{t('cteams.filter.status.archived')}</option>
            <option value="">{t('cteams.filter.status.all')}</option>
          </Select>
        </Field>
      </FilterBar>

      {teams !== undefined && teams.length > 0 && <Text role="caption" tone="secondary" as="p" className="tnum">{t('cteams.summary', { n: teams.length, countries: countrySummary })}</Text>}

      <ListTable
        columns={columns}
        rows={teams ?? []}
        rowKey={(team) => team.teamId}
        onRowClick={(team) => void openTeam(team.teamId)}
        {...(detail !== undefined ? { selectedKey: detail.team.teamId } : {})}
        loading={teams === undefined && error === undefined}
        loadingLabel={t('ui.loading')}
        error={error !== undefined ? t('cteams.empty.failed') : undefined}
        onRetry={() => void load()}
        retryLabel={t('ui.retry')}
        empty={<EmptyState title={t('cteams.empty.title')} detail={t('cteams.empty.detail')} />}
        minWidth={1100}
      />

      <Drawer open={drawerOpen} onClose={closeDrawer} labelledBy="cteams-title">
        {detail !== undefined && draft !== undefined && teamDrawer(detail, draft)}
        {creating !== undefined && createDrawer(creating)}
      </Drawer>
    </Stack>
  );
}
